import type MapboxDraw from "@mapbox/mapbox-gl-draw";
import type { ZoneBoundary } from "@sitescene/types";

import {
	geoJsonLineStringToFeature,
	geoJsonPolygonToFeature,
	normalizeGeoJsonLineString,
	normalizeGeoJsonPolygon,
} from "@sitescene/shared";

import {
	boundaryFromDrawFeatures,
	pickRichestDrawFeature,
	shouldKeepRicherLiveDrawBoundary,
	type ZoneBoundaryDrawGeometryType,
} from "./draw-boundary";

export type ZoneBoundaryDrawAction =
	| "change_coordinates"
	| "create"
	| "delete"
	| "move";

export type ZoneBoundaryDrawConfig = {
	boundary?: null | ZoneBoundary;
	/** Zone swatch hex for Draw stroke and vertices. */
	color?: string;
	enabled: boolean;
	featureId: string;
	/** Polygon fill opacity. Omitted or `0` leaves the shape unfilled. */
	fillOpacity?: number;
	geometryType?: ZoneBoundaryDrawGeometryType;
	/** Perimeter / interior stroke width in pixels. */
	lineWidth?: number;
	onChange: (
		boundary: null | ZoneBoundary,
		action?: ZoneBoundaryDrawAction,
	) => void;
	/** Receives a live reader for the geometry in Mapbox Draw, or `null` on teardown. */
	registerCaptureBoundary?: (
		capture: (() => null | ZoneBoundary) | null,
	) => void;
	/** Drag the whole outline to move sensors. Vertex drag unlocks Custom reshape. */
	translateOnly?: boolean;
};

export const getBoundaryFromDraw = (
	draw: MapboxDraw,
	geometryType: ZoneBoundaryDrawGeometryType,
): null | ZoneBoundary =>
	boundaryFromDrawFeatures(draw.getAll().features, geometryType);

export const changeDrawMode = (
	draw: MapboxDraw,
	mode: string,
	options?: Record<string, unknown>,
): void => {
	(
		draw.changeMode as (
			nextMode: string,
			nextOptions?: Record<string, unknown>,
		) => void
	)(mode, options);
};

const deleteDrawFeature = (draw: MapboxDraw, featureId: string): void => {
	(draw.delete as (id: string) => void)(featureId);
};

export const keepLatestGeometry = (
	draw: MapboxDraw,
	_geometryType: ZoneBoundaryDrawGeometryType,
): void => {
	const features = draw.getAll().features;
	const richest = pickRichestDrawFeature(features);
	if (!richest || features.length <= 1) return;

	for (const feature of features) {
		if (feature === richest || feature.id == null) continue;
		deleteDrawFeature(draw, String(feature.id));
	}
};

export const enterDrawMode = (
	draw: MapboxDraw,
	geometryType: ZoneBoundaryDrawGeometryType,
): void => {
	try {
		changeDrawMode(
			draw,
			geometryType === "line" ? "draw_line_string" : "draw_polygon",
		);
	} catch {
		// Leave Draw in its previous mode; the next geometry change retries.
	}
};

const boundaryToFeature = (
	boundary: ZoneBoundary,
	geometryType: ZoneBoundaryDrawGeometryType,
): GeoJSON.Feature<GeoJSON.LineString | GeoJSON.Polygon> | null => {
	if (geometryType === "line") {
		const line = normalizeGeoJsonLineString(boundary);
		return line ? geoJsonLineStringToFeature(line) : null;
	}

	const polygon = normalizeGeoJsonPolygon(boundary);
	return polygon ? geoJsonPolygonToFeature(polygon) : null;
};

export const serializeDrawSyncState = ({
	boundary,
	geometryType,
	translateOnly,
}: {
	boundary: null | ZoneBoundary;
	geometryType: ZoneBoundaryDrawGeometryType;
	translateOnly: boolean;
}): string =>
	JSON.stringify({
		boundary,
		geometryType,
		translateOnly: Boolean(translateOnly),
	});

/** True when Draw already shows the config geometry — skip delete/re-add. */
const isLiveDrawBoundaryInSync = (
	liveBoundary: null | ZoneBoundary,
	configBoundary: null | undefined | ZoneBoundary,
): boolean =>
	JSON.stringify(liveBoundary) === JSON.stringify(configBoundary ?? null);

const getDrawMode = (draw: MapboxDraw): string => {
	try {
		return (draw.getMode as () => string)();
	} catch {
		return "simple_select";
	}
};

/** Push React config geometry into Mapbox Draw without dropping edit mode when possible. */
export const syncDrawBoundary = (
	draw: MapboxDraw,
	config: ZoneBoundaryDrawConfig,
	lastSyncedBoundaryRef: { current: null | string },
	isApplyingDrawUpdateRef: { current: boolean },
): void => {
	const geometryType = config.geometryType ?? "polygon";
	const translateOnly = Boolean(config.translateOnly);
	const serialized = serializeDrawSyncState({
		boundary: config.boundary ?? null,
		geometryType,
		translateOnly,
	});
	if (serialized === lastSyncedBoundaryRef.current) {
		return;
	}

	const liveBoundary = getBoundaryFromDraw(draw, geometryType);

	// Draw already matches React state (e.g. after emitBoundary). Do not
	// deleteAll/re-add — that forces simple_select and drops vertex edit mode.
	if (isLiveDrawBoundaryInSync(liveBoundary, config.boundary)) {
		lastSyncedBoundaryRef.current = serialized;
		return;
	}

	if (
		shouldKeepRicherLiveDrawBoundary({
			configBoundary: config.boundary,
			geometryType,
			liveBoundary,
			translateOnly,
		})
	) {
		lastSyncedBoundaryRef.current = serializeDrawSyncState({
			boundary: liveBoundary,
			geometryType,
			translateOnly,
		});
		if (!isApplyingDrawUpdateRef.current && liveBoundary) {
			config.onChange(liveBoundary, "change_coordinates");
		}
		return;
	}

	const previousMode = getDrawMode(draw);
	isApplyingDrawUpdateRef.current = true;

	try {
		draw.deleteAll();

		const feature = config.boundary
			? boundaryToFeature(config.boundary, geometryType)
			: null;

		if (feature) {
			feature.id = config.featureId;
			const ids = draw.add(feature);
			const featureId = ids[0];
			if (featureId) {
				// Keep direct_select after an external sync so vertex editing
				// survives Auto → Custom unlock and parent re-renders.
				if (previousMode === "direct_select" && !translateOnly) {
					changeDrawMode(draw, "direct_select", { featureId });
				} else {
					changeDrawMode(draw, "simple_select", {
						featureIds: [featureId],
					});
				}
			}
		} else if (!translateOnly) {
			enterDrawMode(draw, geometryType);
		}

		lastSyncedBoundaryRef.current = serialized;
	} catch {
		// Leave Draw in its previous mode; the next change retries sync.
	} finally {
		isApplyingDrawUpdateRef.current = false;
	}
};
